// ==========================================
// GEOLOCATION & ENTFERNUNGSBERECHNUNG
// ==========================================

import { isNativeApp, openLocationSettings } from './nativeSettings';
// Capacitor Import für native GPS
import { Geolocation } from '@capacitor/geolocation';

// ==========================================
// BROWSER GPS-Funktion (für Web)
// ==========================================
const getBrowserLocation = () => {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('GPS wird von diesem Browser nicht unterstützt.'));
      return;
    }
    
    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          lat: position.coords.latitude,
          lng: position.coords.longitude
        });
      },
      (error) => {
        switch (error.code) {
          case error.PERMISSION_DENIED:
            reject(new Error('GPS_PERMISSION_DENIED'));
            break;
          case error.POSITION_UNAVAILABLE:
            reject(new Error('GPS_UNAVAILABLE'));
            break;
          case error.TIMEOUT:
            reject(new Error('GPS_TIMEOUT'));
            break;
          default:
            reject(new Error('GPS_UNKNOWN_ERROR'));
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 0
      }
    );
  });
};

// ==========================================
// CAPACITOR GPS-Funktion (für Native App)
// ==========================================
const getNativeLocation = async () => {
  // 🔐 SCHRITT 1: Prüfe aktuelle Permissions
  const permission = await Geolocation.checkPermissions();
  
  // 🔐 SCHRITT 2: Fordere Permissions an, falls nicht vorhanden
  if (permission.location !== 'granted') {
    const request = await Geolocation.requestPermissions();
    
    if (request.location !== 'granted') {
      throw new Error('GPS_PERMISSION_DENIED');
    }
  }
  
  // SCHRITT 3: Nutze Capacitor Geolocation
  const position = await Geolocation.getCurrentPosition({
    enableHighAccuracy: true,
    timeout: 15000,
    maximumAge: 0
  });
  
  return {
    lat: position.coords.latitude,
    lng: position.coords.longitude
  };
};

// ==========================================
// HAUPT-FUNKTION: Plattform-Erkennung
// ==========================================
export const getUserLocation = async () => {
  try {
    // 🔀 Entscheide basierend auf Plattform
    if (isNativeApp()) {
      // 📱 Native App: Capacitor Geolocation
      return await getNativeLocation();
    } else {
      // 🌐 Web Browser: Native Browser API
      return await getBrowserLocation();
    }
} catch (error) {
    // Einheitliche Fehlerbehandlung
    if (error.message === 'GPS_PERMISSION_DENIED' || error.message.includes('User denied') || error.message.includes('denied')) {
      throw new Error('🚫 GPS-Zugriff verweigert. Bitte erlaube den Standortzugriff in den Einstellungen.');
    } else if (error.message === 'GPS_TIMEOUT' || error.message.includes('timeout')) {
      throw new Error('⏱️ Zeitüberschreitung bei der Standortermittlung. Bitte versuche es erneut.');
    } else if (error.message === 'GPS_UNAVAILABLE' || error.message.includes('unavailable') || error.message.includes('disabled')) {
      throw new Error('📍 GPS ist deaktiviert. Bitte aktiviere GPS in den Einstellungen.');
    } else {
      throw new Error('❌ GPS-Standort konnte nicht ermittelt werden: ' + error.message);
    }
  }
};

// Öffne Einstellungen (nur native App)
export const openDeviceSettings = async () => {
  return await openLocationSettings();
};

// Entfernung zwischen zwei Koordinaten berechnen (Haversine-Formel)
// Gibt Entfernung in Metern zurück
export const calculateDistance = (lat1, lng1, lat2, lng2) => {
  const R = 6371e3; // Erdradius in Metern
  const φ1 = (lat1 * Math.PI) / 180;
  const φ2 = (lat2 * Math.PI) / 180;
  const Δφ = ((lat2 - lat1) * Math.PI) / 180;
  const Δλ = ((lng2 - lng1) * Math.PI) / 180;
  
  const a =
    Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
    Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  
  return R * c; // Entfernung in Metern
};

// Entfernung formatieren (Meter oder Kilometer)
export const formatDistance = (meters) => {
  if (meters < 1000) {
    return `${Math.round(meters)}m`;
  } else {
    return `${(meters / 1000).toFixed(1)}km`;
  }
};

// Toiletten nach Entfernung sortieren
export const sortToiletsByDistance = (toilets, userLat, userLng) => {
  return toilets
    .map(toilet => ({
      ...toilet,
      distance: calculateDistance(userLat, userLng, toilet.lat, toilet.lng)
    }))
    .sort((a, b) => a.distance - b.distance);
};